import React, { useContext, useState, useEffect, useMemo } from "react";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import { FaBook } from "react-icons/fa";
import Table from "../../UI/Table/Table";
import { PaginationType } from "../../Types/APITypes";
import Alert from "../../UI/Alert/Alert";
import { NotifyContext } from "../../context/notifyContext";
import { useNavigate } from "react-router-dom";
import BookingService from "../../services/api/BookingService";
import { Booking, BookingFilters } from "../../Types/Dashboard";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

export default function BookingList() {
  useBreadcrumbs([
    {
      label: "Sessions",
      icon: <FaBook />,
      is_link: true,
      href: "/app/bookings",
    },
    {
      label: "Liste",
      is_link: false,
    },
  ]);

  const notify = useContext(NotifyContext);
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });
  const [filters, setFilters] = useState<BookingFilters>({
    page: 1,
    per_page: 15,
  });

  const fetchData = React.useCallback(
    (paginationData?: PaginationType) => {
      const paginationToUse = paginationData || pagination;
      setLoading(true);
      setError("");

      BookingService.getBookings({
        ...filters,
        page: paginationToUse.current_page,
        per_page: paginationToUse.per_page,
      })
        .then((response) => {
          setBookings(response.data.bookings);
          setPagination(response.data.meta);
        })
        .catch((err) => {
          setError(err?.message || "Erreur lors du chargement des sessions");
          notify(err?.message || "Erreur lors du chargement des sessions", "error");
        })
        .finally(() => {
          setLoading(false);
        });
    },
    // Stable across renders unless filters or notify change
    [filters, notify]
  );

  useEffect(() => {
    fetchData();
  }, [filters, fetchData]);

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "dd MMM yyyy, HH:mm", { locale: fr });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "confirmed":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Étudiant",
        Cell: (params: any) => {
          const { row } = params;
          const booking: Booking = row?.original;
          return (
            <p>
              {booking.student?.first_name} {booking.student?.last_name}
            </p>
          );
        },
      },
      {
        Header: "Tuteur",
        Cell: (params: any) => {
          const { row } = params;
          const booking: Booking = row?.original;
          return (
            <p>
              {booking.tutor?.first_name} {booking.tutor?.last_name}
            </p>
          );
        },
      },
      {
        Header: "Date de début",
        Cell: (params: any) => {
          const { row } = params;
          const booking: Booking = row?.original;
          return <p>{formatDate(booking.start_time)}</p>;
        },
      },
      {
        Header: "Statut",
        Cell: (params: any) => {
          const { row } = params;
          const booking: Booking = row?.original;
          return (
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                booking.status
              )}`}
            >
              {booking.status}
            </span>
          );
        },
      },
      {
        Header: "Coût (crédits)",
        accessor: "credits_cost",
      },
      {
        Header: "Actions",
        Cell: (params: any) => {
          const { row } = params;
          const booking: Booking = row?.original;
          return (
            <button
              onClick={() => navigate(`/app/bookings/${booking.id}`)}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              Voir détails
            </button>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <div className="flex flex-col overflow-auto">
      <div className="flex flex-col flex-1 p-6">
        {error && <Alert color="error">{error}</Alert>}

        <div className="mb-4 flex gap-4">
          <select
            value={filters.status || ""}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value || undefined, page: 1 })
            }
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="confirmed">Confirmé</option>
            <option value="completed">Terminé</option>
            <option value="cancelled">Annulé</option>
          </select>
        </div>

        <div className="bg-white shadow rounded overflow-hidden">
          <Table
            columns={columns as any}
            data={bookings}
            fetchData={fetchData}
            loading={loading}
            pageCount={pagination.last_page || 1}
            total={pagination.total || 0}
          />
        </div>
      </div>
    </div>
  );
}
